import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { map, isEmpty, omitBy, isNil } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { routeToBoxesView } from "Helpers/index";
import useSession from "Hooks/session.hook";
import orderStatusesMap from "Data/order-statuses.json";
import { Row, Col } from "antd";
import { DataTable } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";

export interface Option {
  label: string;
  value: string;
}

export interface LastMileDriversListProps {
  parcelList?: any;
  onFilter?: any;
  driverList?: any;
}

const LastMileDriversList: React.FC<LastMileDriversListProps> = ({
  parcelList,
  onFilter,
  driverList,
}) => {
  /* TODO: Enhancement: This list works fine as it is, but the requirement for
  this was a bit iffy for the filters, so it might change in the future */
  const { roles } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const initialFilter: any = {
    type: "",
    status: "",
    sortingHubID: "",
    driverID: "",
    unassignedDriver: "",
  };
  const [filterState, setFilterState] = useState<{
    [key: string]: string | boolean;
  }>(initialFilter);

  const initialFilterSelection = [
    {
      key: "status",
      name: "status",
      label: t("placeholder.byStatus"),
      placeholder: t("placeholder.byStatus"),
      value: filterState?.status,
      options: [
        { label: t("option.all"), value: "" },
        {
          label: t("label.deliveryNotPossible"),
          value: "DELIVERY_NOT_POSSIBLE",
        },
        { label: t("label.deliveryInProgress"), value: "DELIVERY_IN_PROGRESS" },
        { label: t("label.deliveryRefused"), value: "DELIVERY_REFUSED" },
        { label: t("label.unloaded"), value: "UNLOADED" },
      ],
    },
    {
      key: "driverID",
      name: "driverID",
      label: t("label.byDriver"),
      placeholder: t("placeholder.byDriver"),
      value: filterState?.driverID,
      options: [{ label: t("option.all"), value: "" }].concat(driverList),
    },
  ];

  const [filterSelection, setFilterSelection] = useState<any>(
    initialFilterSelection,
  );

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      customerOrgList: selection.customerOrgList,
      sortingHub: selection.sortingHub,
      pickupType: selection.pickupType,
      status: selection.status,
      destination: selection.destination,
      driverID: selection.driverID,
      unassignedDriver: selection.driverID === "UNASSIGNED" && true,
    }));
  };

  const handleOpenBoxesTrackingView = (boxID: string) => {
    routeToBoxesView(boxID, location, history);
  };

  const columns: any = [
    {
      title: t("label.driver").toLocaleUpperCase(),
      dataIndex: "name",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end ">
            <span
              className="half-square custom-icon mb-5"
              style={{
                borderLeft: `12px solid transparent`,
                borderBottom: `12px solid #E8A51A`,
              }}
            ></span>
            <span style={{ padding: "0 4px" }}>
              {typeof record.assignedDriver === "string"
                ? record.assignedDriverData
                  ? record?.assignedDriverData?.fullName
                  : record?.assignedDriver
                : t("label.unavailable")}
            </span>
          </div>
        );
      },
    },
    {
      title: t("label.boxID").toLocaleUpperCase(),
      dataIndex: ["boxParcels", "boxCode"],
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div
            className="clickable-id"
            style={{ display: "flex", flexDirection: "column", gap: "8px" }}
          >
            {map(record.boxParcels, (boxes) => {
              return (
                <button
                  className="button-link"
                  onClick={() => handleOpenBoxesTrackingView(boxes._id)}
                  style={{ width: "fit-content" }}
                >
                  {boxes.boxCode || ""}
                </button>
              );
            })}
          </div>
        );
      },
    },
    {
      title: t("label.boxesAssigned").toLocaleUpperCase(),
      dataIndex: "boxParcels",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.boxParcels?.length}</span>;
      },
    },

    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "deliveryStatus",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {map(record.boxParcels, (boxes) => {
              const status =
                orderStatusesMap?.[boxes.status]?.[roles[0]["type"]];

              return (
                <span
                  className="status-indicator"
                  style={getStatusStyle(status)}
                >
                  {status ? toCapitalisedSpaced(status) : ""}
                </span>
              );
            })}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  useEffect(() => {
    if (!isEmpty(driverList))
      setFilterSelection && setFilterSelection(initialFilterSelection);
  }, [driverList]);

  return (
    <ErrorBoundary>
      <div className="parcel-order-list">
        <DataTable
          rowKey={(record: any, index) => {
            return index;
          }}
          columns={columns}
          scroll={{ x: true }}
          dataSource={parcelList || []}
          title={() => (
            <Row>
              <Col span={16} className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.searchByBox")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
              </Col>
            </Row>
          )}
        />
      </div>
    </ErrorBoundary>
  );
};

export default LastMileDriversList;
